import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { getPosition } from '../../lib/geo';
import geomagnetism from 'geomagnetism';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, Compass, AlertCircle, Smartphone, MapPin, RefreshCw, Check, Camera, X, ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';

// Coordonnées de la Kaaba (La Mecque).
const KAABA = { lat: 21.4225, lon: 39.8262 };

// Rose des vents à 16 points (français : O = Ouest).
const COMPASS_16 = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'];
/** Abréviation cardinale (N, ENE, …) pour un cap en degrés. */
const cardinal = (deg) => COMPASS_16[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16];

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

// Points cardinaux (16 secteurs) pour libeller un cap en degrés.
const CARDINALS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'];
const cardinalLabel = (deg) => CARDINALS[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16];

/**
 * Déclinaison magnétique (écart Nord magnétique → Nord géographique) au point
 * donné, via le World Magnetic Model embarqué (geomagnetism). En degrés :
 * positif = Est. La boussole d'un téléphone lit le Nord MAGNÉTIQUE ; le cap de
 * la Qibla est calculé depuis le Nord GÉOGRAPHIQUE — il faut donc convertir.
 * Renvoie 0 si le modèle échoue (aucune régression : comportement d'origine).
 */
const getDeclination = (lat, lon) => {
    try {
        return geomagnetism.model().point([lat, lon]).decl || 0;
    } catch {
        return 0;
    }
};

/** Cap great-circle initial entre deux points GPS, en degrés [0,360) depuis le Nord. */
const computeBearing = (lat1, lon1, lat2, lon2) => {
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δλ = toRad(lon2 - lon1);
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

/** Distance orthodromique (km) entre deux points GPS. */
const haversineKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** Plus petit écart signé entre deux angles (degrés), dans [-180, 180]. */
const shortestDelta = (from, to) => ((to - from + 540) % 360) - 180;
/** Distance angulaire absolue (degrés), dans [0, 180]. */
const angularDistance = (a, b) => Math.abs(shortestDelta(a, b));

const ALIGN_THRESHOLD = 6; // tolérance d'alignement en degrés
const AR_FOV = 60; // champ de vision horizontal supposé de la caméra (°)

/**
 * Azimut horizontal (depuis le Nord magnétique) de l'axe de visée de la caméra
 * arrière (−Z de l'appareil), calculé depuis l'orientation absolue alpha/beta/gamma.
 *
 * On construit la matrice de rotation appareil→monde (convention W3C) puis on
 * projette le vecteur caméra au sol : robuste même téléphone à la verticale, là
 * où l'extraction directe d'un cap depuis `alpha` souffre du gimbal lock.
 * Renvoie null si l'orientation est incomplète.
 */
const cameraAzimuthFromOrientation = (alpha, beta, gamma) => {
    if (alpha == null || beta == null || gamma == null) return null;
    const a = toRad(alpha), b = toRad(beta), g = toRad(gamma);
    const cA = Math.cos(a), sA = Math.sin(a);
    const cB = Math.cos(b), sB = Math.sin(b);
    const cG = Math.cos(g), sG = Math.sin(g);
    // Caméra (−Z appareil) dans le repère monde (Est, Nord, Haut) = −(3e colonne de R).
    const east = -(cA * sG + cG * sA * sB);
    const north = -(sA * sG - cA * cG * sB);
    return (toDeg(Math.atan2(east, north)) + 360) % 360;
};

export const Qibla = () => {
    const [qiblaBearing, setQiblaBearing] = useState(null); // cap vers la Kaaba depuis le Nord GÉOGRAPHIQUE
    const [heading, setHeading] = useState(null); // orientation MAGNÉTIQUE lissée de l'appareil
    const [declination, setDeclination] = useState(0); // écart Nord magnétique → géographique (°, + = Est)
    const [coords, setCoords] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasSensor, setHasSensor] = useState(false);
    const [needsPermission, setNeedsPermission] = useState(false);
    const [accuracy, setAccuracy] = useState(null); // précision boussole iOS (°), null si inconnue

    // Mode Réalité Augmentée (caméra)
    const [arActive, setArActive] = useState(false);
    const [arError, setArError] = useState(null);
    const [arStarting, setArStarting] = useState(false);
    const [arOpen, setArOpen] = useState(false);
    const [, setArDeltaRaf] = useState(null); // écart signé caméra→Qibla (raf) — non utilisé par l'overlay (calcul local)

    const rawHeadingRef = useRef(null); // dernier cap brut (sans render)
    const smoothRef = useRef(null); // cap lissé courant
    const wasAlignedRef = useRef(false);
    const orientRef = useRef({ alpha: null, beta: null, gamma: null, compass: null }); // orientation brute (RA)
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const arActiveRef = useRef(false); // miroir de arActive pour la boucle RAF
    const declinationRef = useRef(0);
    const qiblaRef = useRef(null);
    const arHasCamera = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

    // Réception des événements d'orientation : on ne stocke que dans un ref (pas de render).
    // IMPORTANT : on n'accepte qu'une orientation ABSOLUE (référencée au Nord). Sur
    // Android, l'événement `deviceorientation` (non absolu) renvoie un alpha relatif
    // qui dérive et donnerait une Qibla fausse — on l'ignore (event.absolute !== true).
    const handleOrientation = useCallback((event) => {
        let h;
        if (typeof event.webkitCompassHeading === 'number' && !Number.isNaN(event.webkitCompassHeading)) {
            h = event.webkitCompassHeading; // iOS : cap magnétique direct
            // webkitCompassAccuracy : < 0 ou élevé = boussole à calibrer.
            if (typeof event.webkitCompassAccuracy === 'number') setAccuracy(event.webkitCompassAccuracy);
        } else if (event.absolute === true && typeof event.alpha === 'number' && event.alpha !== null) {
            h = (360 - event.alpha) % 360; // Android (absolu) : alpha → cap magnétique
        } else {
            return; // orientation non absolue / inutilisable : ignorée
        }
        if (typeof h === 'number' && !Number.isNaN(h)) {
            rawHeadingRef.current = h;
            // Conserver l'orientation complète pour la RA (azimut caméra à la verticale).
            orientRef.current = {
                alpha: event.alpha,
                beta: event.beta,
                gamma: event.gamma,
                compass: typeof event.webkitCompassHeading === 'number' ? event.webkitCompassHeading : null
            };
            if (!hasSensor) setHasSensor(true);
        }
    }, [hasSensor]);

    // Boucle de lissage (low-pass anti-tremblement, gère le passage 0/360).
    useEffect(() => {
        let raf;
        const tick = () => {
            const raw = rawHeadingRef.current;
            if (raw != null) {
                const prev = smoothRef.current;
                const next = prev == null
                    ? raw
                    : (prev + shortestDelta(prev, raw) * 0.18 + 360) % 360;
                smoothRef.current = next;
                setHeading((cur) => (cur == null || angularDistance(cur, next) >= 0.4 ? next : cur));
            }

            // Mode RA : azimut de la caméra → écart signé vers la Qibla.
            if (arActiveRef.current && qiblaRef.current != null) {
                const o = orientRef.current;
                // iOS : webkitCompassHeading reste un cap horizontal valable même appareil dressé.
                // Android : azimut caméra reconstruit depuis alpha/beta/gamma (anti gimbal lock).
                const camMag = o.compass != null ? o.compass : cameraAzimuthFromOrientation(o.alpha, o.beta, o.gamma);
                if (camMag != null) {
                    const camTrue = (camMag + declinationRef.current + 360) % 360;
                    const delta = shortestDelta(camTrue, qiblaRef.current); // + = Qibla à droite
                    setArDeltaRaf((cur) => (cur == null || Math.abs(cur - delta) >= 0.4 ? delta : cur));
                }
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    // Garder les refs (lues dans la boucle RAF) synchronisées avec l'état.
    useEffect(() => { declinationRef.current = declination; }, [declination]);
    useEffect(() => { qiblaRef.current = qiblaBearing; }, [qiblaBearing]);
    useEffect(() => { arActiveRef.current = arActive; }, [arActive]);

    // Active l'écoute du capteur (avec permission iOS si nécessaire). Renvoie true si OK.
    const enableSensor = useCallback(async () => {
        if (
            typeof DeviceOrientationEvent !== 'undefined' &&
            typeof DeviceOrientationEvent.requestPermission === 'function'
        ) {
            try {
                const result = await DeviceOrientationEvent.requestPermission();
                if (result !== 'granted') {
                    setError("Permission d'accès à la boussole refusée.");
                    return false;
                }
            } catch {
                setError("La boussole n'est pas accessible sur cet appareil.");
                return false;
            }
        }
        setNeedsPermission(false);
        window.addEventListener('deviceorientationabsolute', handleOrientation, true);
        window.addEventListener('deviceorientation', handleOrientation, true);
        return true;
    }, [handleOrientation]);

    // Géolocalisation → cap + distance (permission native demandée sur mobile).
    const locate = useCallback(() => {
        setLoading(true);
        setError(null);
        getPosition({ highAccuracy: true })
            .then(({ lat, lon }) => {
                setCoords({ lat, lon });
                setQiblaBearing(computeBearing(lat, lon, KAABA.lat, KAABA.lon));
                // Déclinaison magnétique locale via le World Magnetic Model
                // (calcul embarqué, hors-ligne). Repli à 0° si indisponible.
                try {
                    const info = geomagnetism.model().point([lat, lon]);
                    const decl = Number(info?.decl) || 0;
                    declinationRef.current = decl;
                    setDeclination(decl);
                } catch {
                    declinationRef.current = 0;
                    setDeclination(0);
                }
                setLoading(false);
            })
            .catch((err) => {
                setError(
                    err?.message === 'permission_denied'
                        ? "Accès à la position refusé. Autorisez la localisation dans les réglages pour calculer la Qibla."
                        : "Géolocalisation indisponible. Autorisez l'accès à votre position pour calculer la Qibla."
                );
                setLoading(false);
            });
    }, []);

    useEffect(() => { locate(); }, [locate]);

    // Détecte si une permission de capteur est requise (iOS), sinon écoute directement (Android).
    useEffect(() => {
        if (
            typeof DeviceOrientationEvent !== 'undefined' &&
            typeof DeviceOrientationEvent.requestPermission === 'function'
        ) {
            setNeedsPermission(true);
        } else if (typeof window !== 'undefined' && 'DeviceOrientationEvent' in window) {
            window.addEventListener('deviceorientationabsolute', handleOrientation, true);
            window.addEventListener('deviceorientation', handleOrientation, true);
        }
        return () => {
            window.removeEventListener('deviceorientationabsolute', handleOrientation, true);
            window.removeEventListener('deviceorientation', handleOrientation, true);
        };
    }, [handleOrientation]);

    // Angles d'affichage.
    const hasHeading = heading != null;
    // Cap GÉOGRAPHIQUE de l'appareil = cap magnétique + déclinaison. C'est lui qu'on
    // compare au cap de la Qibla (également géographique) pour un alignement correct.
    const trueHeading = hasHeading ? (heading + declination + 360) % 360 : null;
    // Direction de la Kaaba sur l'écran : relative à l'orientation si capteur, sinon absolue (N en haut).
    const kaabaAngle = qiblaBearing != null
        ? (hasHeading ? (qiblaBearing - trueHeading + 360) % 360 : qiblaBearing)
        : 0;
    const roseRotation = hasHeading ? -trueHeading : 0;
    const aligned = hasHeading && qiblaBearing != null && angularDistance(kaabaAngle, 0) <= ALIGN_THRESHOLD;
    const distanceKm = coords ? haversineKm(coords.lat, coords.lon, KAABA.lat, KAABA.lon) : null;
    // Boussole peu fiable (iOS) : précision négative ou supérieure à 20°.
    const poorAccuracy = accuracy != null && (accuracy < 0 || accuracy > 20);
    // Alias pour l'alerte de calibration HEAD.
    const compassAccuracy = accuracy;

    // --- Réalité augmentée ---
    // Écart signé entre le cap courant et la Qibla : >0 ⇒ tourner à droite.
    const arDelta = (hasHeading && qiblaBearing != null) ? shortestDelta(heading, qiblaBearing) : null;
    const AR_FOV = 60; // champ visuel horizontal approché de la caméra (°)
    const arOnScreen = arDelta != null && Math.abs(arDelta) <= AR_FOV / 2;
    // Position horizontale du marqueur Kaaba (5 %–95 % de l'écran).
    const arLeftPct = arDelta != null ? 50 + Math.max(-1, Math.min(1, arDelta / (AR_FOV / 2))) * 45 : 50;

    const closeAR = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        setArOpen(false);
    }, []);

    const openAR = useCallback(async () => {
        setArError(null);
        try {
            // Sur natif : s'assurer que la permission caméra OS est accordée pour
            // que la WebView autorise getUserMedia (best-effort).
            if (Capacitor.isNativePlatform()) {
                try {
                    const { Camera } = await import('@capacitor/camera');
                    const perm = await Camera.checkPermissions();
                    if (perm?.camera !== 'granted') {
                        await Camera.requestPermissions({ permissions: ['camera'] });
                    }
                } catch { /* best-effort : on tente quand même getUserMedia */ }
            }
            // Caméra arrière en priorité (idéal pour viser l'horizon).
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' } },
                audio: false,
            });
            streamRef.current = stream;
            setArOpen(true);
        } catch (e) {
            setArError("Caméra inaccessible. Autorisez l'accès à la caméra dans les réglages.");
        }
    }, []);

    // Branche le flux sur l'élément vidéo une fois l'overlay monté.
    useEffect(() => {
        if (arOpen && videoRef.current && streamRef.current) {
            videoRef.current.srcObject = streamRef.current;
            videoRef.current.play?.().catch(() => {});
        }
    }, [arOpen]);

    // Libère la caméra si le composant est démonté overlay ouvert.
    useEffect(() => () => {
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    }, []);

    // Vibration unique à l'entrée dans l'alignement.
    useEffect(() => {
        if (aligned && !wasAlignedRef.current) {
            navigator.vibrate?.(80);
            wasAlignedRef.current = true;
        } else if (!aligned) {
            wasAlignedRef.current = false;
        }
    }, [aligned]);

    return (
        <>
        <Card className="rounded-3xl border-0 shadow-lg bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/40 h-full">
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between gap-2 text-lg font-bold text-indigo-700 dark:text-indigo-300">
                    <span className="flex items-center gap-2">
                        <Compass className="h-5 w-5" />
                        Direction de la Qibla
                    </span>
                    {qiblaBearing != null && (
                        <button
                            onClick={locate}
                            className="text-muted-foreground transition-colors hover:text-indigo-600"
                            aria-label="Recalculer ma position"
                            title="Recalculer ma position"
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>
                    )}
                </CardTitle>
            </CardHeader>

            <CardContent className="flex flex-col items-center gap-5 pb-6">
                {loading && (
                    <div className="flex flex-col items-center justify-center gap-3 py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                        <p className="text-sm text-muted-foreground">Localisation en cours…</p>
                    </div>
                )}

                {!loading && error && (
                    <Alert variant="destructive" className="bg-red-50 dark:bg-red-950/30">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="flex flex-col gap-3">
                            {error}
                            <Button onClick={locate} size="sm" variant="secondary" className="w-fit gap-2">
                                <RefreshCw className="h-3.5 w-3.5" /> Réessayer
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}

                {!loading && !error && qiblaBearing != null && (
                    <>
                        {/* Bannière d'alignement */}
                        {hasSensor && (
                            <div
                                className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                    aligned
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                        : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                                }`}
                            >
                                {aligned ? (
                                    <><Check className="h-4 w-4" /> Vous faites face à la Qibla 🕋</>
                                ) : (
                                    <>Tournez jusqu'à aligner le 🕋 en haut</>
                                )}
                            </div>
                        )}

                        {/* Boussole */}
                        <div
                            className={`relative h-60 w-60 rounded-full border-4 shadow-inner transition-colors duration-300 ${
                                aligned
                                    ? 'border-emerald-400 bg-emerald-50/70 dark:border-emerald-600 dark:bg-emerald-950/40'
                                    : 'border-indigo-200 bg-white/60 dark:border-indigo-800 dark:bg-indigo-950/40'
                            }`}
                        >
                            {/* Repère fixe en haut (direction que vous regardez / Nord sur bureau) */}
                            <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2">
                                <div className={`h-0 w-0 border-x-8 border-t-[14px] border-x-transparent ${aligned ? 'border-t-emerald-500' : 'border-t-indigo-500'}`} />
                            </div>

                            {/* Rose des vents (tourne avec l'appareil) */}
                            <div
                                className="absolute inset-0 transition-transform duration-150 ease-out"
                                style={{ transform: `rotate(${roseRotation}deg)` }}
                            >
                                <svg viewBox="0 0 100 100" className="h-full w-full">
                                    {/* Graduations tous les 15° */}
                                    {Array.from({ length: 24 }).map((_, i) => {
                                        const major = i % 6 === 0;
                                        const a = toRad(i * 15);
                                        const r1 = 46;
                                        const r2 = major ? 40 : 43;
                                        return (
                                            <line
                                                key={i}
                                                x1={50 + r1 * Math.sin(a)}
                                                y1={50 - r1 * Math.cos(a)}
                                                x2={50 + r2 * Math.sin(a)}
                                                y2={50 - r2 * Math.cos(a)}
                                                stroke="currentColor"
                                                strokeWidth={major ? 1.4 : 0.7}
                                                className="text-indigo-300 dark:text-indigo-700"
                                            />
                                        );
                                    })}
                                    {/* Lettres cardinales */}
                                    <text x="50" y="16" textAnchor="middle" className="fill-red-500 text-[8px] font-bold">N</text>
                                    <text x="86" y="53" textAnchor="middle" className="fill-indigo-400 text-[7px] font-semibold">E</text>
                                    <text x="50" y="90" textAnchor="middle" className="fill-indigo-400 text-[7px] font-semibold">S</text>
                                    <text x="14" y="53" textAnchor="middle" className="fill-indigo-400 text-[7px] font-semibold">O</text>
                                </svg>
                            </div>

                            {/* Marqueur Kaaba (direction de la Qibla) */}
                            <div
                                className="absolute inset-0 transition-transform duration-150 ease-out"
                                style={{ transform: `rotate(${kaabaAngle}deg)` }}
                            >
                                {/* Aiguille vers la Kaaba */}
                                <div className="absolute left-1/2 top-1/2 h-[42%] w-1 -translate-x-1/2 -translate-y-full rounded-full bg-gradient-to-t from-transparent to-emerald-500" />
                                {/* Pastille Kaaba au bord */}
                                <div className="absolute left-1/2 top-1 -translate-x-1/2 -translate-y-1/2">
                                    <div className={`flex h-7 w-7 items-center justify-center rounded-full text-sm shadow-md ${aligned ? 'bg-emerald-500' : 'bg-indigo-600'}`}>
                                        🕋
                                    </div>
                                </div>
                            </div>

                            {/* Moyeu central */}
                            <div className="absolute left-1/2 top-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-700 ring-2 ring-white dark:ring-indigo-950" />
                        </div>

                        {/* Mesures */}
                        <div className="grid w-full grid-cols-2 gap-3 text-center">
                            <div className="rounded-2xl bg-white/70 p-3 dark:bg-indigo-950/40">
                                <p className="text-2xl font-bold tabular-nums text-indigo-700 dark:text-indigo-300">
                                    {Math.round(qiblaBearing)}° <span className="text-base font-semibold">{cardinal(qiblaBearing)}</span>
                                </p>
                                <p className="text-xs text-muted-foreground">Qibla · Nord géographique</p>
                            </div>
                            <div className="rounded-2xl bg-white/70 p-3 dark:bg-indigo-950/40">
                                <p className="text-2xl font-bold tabular-nums text-indigo-700 dark:text-indigo-300">
                                    {distanceKm != null ? `${Math.round(distanceKm).toLocaleString()}` : '—'}
                                    <span className="text-sm font-medium"> km</span>
                                </p>
                                <p className="text-xs text-muted-foreground">jusqu'à La Mecque</p>
                            </div>
                        </div>

                        {/* Mode Réalité Augmentée (caméra) — quand la boussole est active. */}
                        {hasSensor && (
                            <Button onClick={openAR} className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700">
                                <Camera className="h-4 w-4" /> Voir en réalité augmentée
                            </Button>
                        )}
                        {arError && !arOpen && (
                            <p className="-mt-2 text-center text-xs text-rose-600">{arError}</p>
                        )}

                        {hasSensor && (
                            <p className="text-xs text-muted-foreground">
                                Cap actuel : <strong className="tabular-nums">{Math.round(heading)}° {cardinal(heading)}</strong>
                                {declination !== 0 && (
                                    <span className="opacity-80">
                                        {' '}· déclinaison corrigée {declination > 0 ? '+' : ''}{declination.toFixed(1)}°
                                    </span>
                                )}
                            </p>
                        )}

                        {/* Avertissement de calibration (boussole iOS imprécise) */}
                        {compassAccuracy != null && (compassAccuracy < 0 || compassAccuracy > 15) && (
                            <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/30">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                    Boussole peu précise. Calibrez-la en dessinant un « 8 » dans l'air, loin de tout objet métallique.
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Demande de permission (iOS) */}
                        {needsPermission && !hasSensor && (
                            <Button onClick={enableSensor} size="sm" className="gap-2 bg-indigo-600 hover:bg-indigo-700">
                                <Compass className="h-4 w-4" /> Activer la boussole
                            </Button>
                        )}

                        {/* Conseil de calibration (capteur actif) */}
                        {hasSensor && (
                            <p className="flex items-start gap-1.5 text-center text-[11px] text-muted-foreground">
                                <Smartphone className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                Tenez le téléphone à plat. Si l'aiguille est instable, calibrez en dessinant un « 8 » dans l'air.
                            </p>
                        )}

                        {/* Repli bureau / pas de capteur */}
                        {!hasSensor && !needsPermission && (
                            <Alert className="border-indigo-200 bg-indigo-50 dark:bg-indigo-950/30">
                                <MapPin className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                    Pas de boussole détectée (ordinateur ?). Orientez le haut de l'écran vers le
                                    <strong> Nord géographique</strong> : la Kaaba 🕋 indique alors la Qibla, à{' '}
                                    <strong>{Math.round(qiblaBearing)}° {cardinal(qiblaBearing)}</strong>. Sur mobile, la boussole est interactive.
                                </AlertDescription>
                            </Alert>
                        )}
                    </>
                )}
            </CardContent>
        </Card>

        {/* ===== Calque Réalité Augmentée (caméra + boussole) ===== */}
        {arOpen && (
            <div className="fixed inset-0 z-[70] select-none bg-black">
                <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-black/20" />

                {/* Fermer */}
                <button
                    onClick={closeAR}
                    className="safe-top absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur"
                    aria-label="Fermer la réalité augmentée"
                >
                    <X className="h-6 w-6" />
                </button>

                {/* Bandeau d'état */}
                <div className="safe-top absolute left-1/2 top-5 z-10 -translate-x-1/2 px-4">
                    <div className={`whitespace-nowrap rounded-full px-4 py-2 text-center text-sm font-semibold shadow-lg backdrop-blur ${aligned ? 'bg-emerald-500/90 text-white' : 'bg-black/55 text-white'}`}>
                        {arDelta == null
                            ? 'Bougez le téléphone pour calibrer…'
                            : aligned
                                ? 'Vous faites face à la Qibla 🕋'
                                : arDelta > 0
                                    ? `Tournez à droite · ${Math.round(Math.abs(arDelta))}°`
                                    : `Tournez à gauche · ${Math.round(Math.abs(arDelta))}°`}
                    </div>
                </div>

                {/* Marqueur Kaaba (dans le champ) ou flèche (hors champ) */}
                {arDelta != null && (arOnScreen ? (
                    <div
                        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-150 ease-out"
                        style={{ left: `${arLeftPct}%` }}
                    >
                        <div className={`flex flex-col items-center gap-2 transition-transform ${aligned ? 'scale-110' : ''}`}>
                            <div className={`flex h-20 w-20 items-center justify-center rounded-2xl text-4xl shadow-2xl ring-4 ${aligned ? 'bg-emerald-500 ring-emerald-300' : 'bg-indigo-600/90 ring-white/40'}`}>
                                🕋
                            </div>
                            {aligned && <span className="rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow">Qibla</span>}
                        </div>
                    </div>
                ) : (
                    <div className={`absolute top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-1 text-white ${arDelta > 0 ? 'right-5' : 'left-5'}`}>
                        {arDelta > 0 ? <ArrowRight className="h-16 w-16 animate-pulse" /> : <ArrowLeft className="h-16 w-16 animate-pulse" />}
                        <span className="text-sm font-bold tabular-nums">{Math.round(Math.abs(arDelta))}°</span>
                    </div>
                ))}

                {/* Réticule central */}
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-16 w-px -translate-x-1/2 -translate-y-1/2 bg-white/40" />
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-px w-16 -translate-x-1/2 -translate-y-1/2 bg-white/40" />

                {/* Infos bas */}
                <div className="safe-bottom absolute bottom-6 left-1/2 z-10 w-full -translate-x-1/2 px-6 text-center text-white">
                    <p className="text-xs text-white/75">Tenez le téléphone vertical et tournez sur vous-même</p>
                    {qiblaBearing != null && (
                        <p className="mt-1 text-sm font-semibold tabular-nums">
                            Qibla {Math.round(qiblaBearing)}° {cardinal(qiblaBearing)}{hasHeading ? ` · cap ${Math.round(heading)}°` : ''}
                        </p>
                    )}
                </div>
            </div>
        )}
        </>
    );
};
